import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Coins, Save, Star, Smile, X, Plus, Trash2 } from "lucide-react";

/**
 * ChannelSubEconomySettings — streamer-facing dashboard panel to configure:
 *   1. Sub pricing (Gold Coins) — flat 100-5000 GC / month
 *   2. Sub-only emote pack — up to 8 emojis with keys+labels that subscribers
 *      see automatically in their EmotePicker inside this channel's chat.
 */
const MIN_GC = 100;
const MAX_GC = 5000;
const MIN_USD = 1;
const MAX_USD = 100;
const MAX_EMOTES = 8;

export default function ChannelSubEconomySettings({ ownerUsername }) {
  const [price, setPrice] = useState(300);
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceUsd, setPriceUsd] = useState(5);
  const [priceUsdBusy, setPriceUsdBusy] = useState(false);
  const [emotes, setEmotes] = useState([]);
  const [emoteBusy, setEmoteBusy] = useState(false);

  useEffect(() => {
    if (!ownerUsername) return;
    api.get(`/channels/${ownerUsername}/sub-pricing`).then(r => setPrice(r.data.sub_price_gc || 300)).catch(() => {});
    api.get(`/channels/${ownerUsername}/sub-pricing-usd`).then(r => setPriceUsd(r.data.sub_price_usd || 5)).catch(() => {});
    api.get(`/channels/${ownerUsername}/sub-emotes`).then(r => setEmotes(r.data.emotes || [])).catch(() => setEmotes([]));
  }, [ownerUsername]);

  const savePrice = async () => {
    const p = Math.max(MIN_GC, Math.min(MAX_GC, Number(price) || 0));
    setPriceBusy(true);
    try {
      const r = await api.patch(`/channels/${ownerUsername}/sub-pricing`, { sub_price_gc: p });
      setPrice(r.data.sub_price_gc);
      toast.success(`Sub price updated to ${r.data.sub_price_gc} GC / 30 days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setPriceBusy(false); }
  };

  const savePriceUsd = async () => {
    const p = Math.max(MIN_USD, Math.min(MAX_USD, Number(priceUsd) || 0));
    setPriceUsdBusy(true);
    try {
      const r = await api.patch(`/channels/${ownerUsername}/sub-pricing-usd`, { sub_price_usd: p });
      setPriceUsd(r.data.sub_price_usd);
      toast.success(`USD sub price updated to $${r.data.sub_price_usd.toFixed(2)} / 30 days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setPriceUsdBusy(false); }
  };

  const addRow = () => {
    if (emotes.length >= MAX_EMOTES) { toast.error(`Max ${MAX_EMOTES} emotes`); return; }
    setEmotes([...emotes, { key: `emote${emotes.length + 1}`, label: "New emote", char: "🔥" }]);
  };
  const removeRow = (i) => setEmotes(emotes.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => {
    const copy = [...emotes];
    if (k === "key") v = v.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 24);
    copy[i] = { ...copy[i], [k]: v };
    setEmotes(copy);
  };

  const saveEmotes = async () => {
    // Basic validation
    const seen = new Set();
    for (const e of emotes) {
      if (!e.key || !e.label || !e.char) { toast.error("Every emote needs a key, label, and character"); return; }
      if (seen.has(e.key)) { toast.error(`Duplicate key "${e.key}"`); return; }
      seen.add(e.key);
    }
    setEmoteBusy(true);
    try {
      const r = await api.patch(`/channels/${ownerUsername}/sub-emotes`, { emotes });
      setEmotes(r.data.emotes || []);
      toast.success(`Sub-only emote pack saved · ${r.data.emotes.length} emotes`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setEmoteBusy(false); }
  };

  return (
    <div className="sc-card p-5 space-y-6" data-testid="channel-sub-economy-settings">
      <div>
        <div className="sc-label mb-1" style={{ color: "var(--sc-pink)" }}>Subscription economy</div>
        <h3 className="sc-heading text-xl font-bold">Channel subs</h3>
        <p className="text-xs text-zinc-500 sc-mono">Configure what viewers pay to sub and what perks they unlock in your chat.</p>
      </div>

      {/* Pricing */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 mb-2">
          <Coins className="w-4 h-4 text-[color:var(--sc-orange)]" />
          <div className="flex-1">
            <div className="text-sm font-bold">Sub price (Gold Coins)</div>
            <div className="text-xs text-zinc-500 sc-mono">Flat 30-day sub cost. Between {MIN_GC} and {MAX_GC} GC.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="sub-price-input"
            type="number"
            min={MIN_GC}
            max={MAX_GC}
            step={10}
            value={price}
            onChange={e => setPrice(e.target.value)}
            className="sc-input w-32 text-right"
          />
          <span className="text-xs text-zinc-500 sc-mono">GC / 30 days</span>
          <button
            onClick={savePrice}
            disabled={priceBusy}
            data-testid="sub-price-save"
            className="sc-btn text-xs inline-flex items-center gap-1.5 ml-auto"
          ><Save className="w-3.5 h-3.5" /> {priceBusy ? "Saving…" : "Save price"}</button>
        </div>
      </div>

      {/* Pricing (USD via Stripe) */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-lg leading-none">💳</span>
          <div className="flex-1">
            <div className="text-sm font-bold">Sub price (USD, real money via Stripe)</div>
            <div className="text-xs text-zinc-500 sc-mono">Alt payment path. Buyers can pay with card instead of GC.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 sc-mono">$</span>
          <input
            data-testid="sub-price-usd-input"
            type="number"
            min={MIN_USD}
            max={MAX_USD}
            step={0.50}
            value={priceUsd}
            onChange={e => setPriceUsd(e.target.value)}
            className="sc-input w-32 text-right"
          />
          <span className="text-xs text-zinc-500 sc-mono">USD / 30 days</span>
          <button
            onClick={savePriceUsd}
            disabled={priceUsdBusy}
            data-testid="sub-price-usd-save"
            className="sc-btn text-xs inline-flex items-center gap-1.5 ml-auto"
          ><Save className="w-3.5 h-3.5" /> {priceUsdBusy ? "Saving…" : "Save USD price"}</button>
        </div>
      </div>

      {/* Sub-only emote pack */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 mb-2">
          <Smile className="w-4 h-4 text-[color:var(--sc-volt)]" />
          <div className="flex-1">
            <div className="text-sm font-bold flex items-center gap-2">Sub-only emote pack <Star className="w-3 h-3 text-[color:var(--sc-volt)]" /></div>
            <div className="text-xs text-zinc-500 sc-mono">Up to {MAX_EMOTES} emojis. Subscribers see them automatically in their chat picker.</div>
          </div>
          <button
            onClick={addRow}
            disabled={emotes.length >= MAX_EMOTES}
            data-testid="sub-emote-add"
            className="sc-btn-ghost text-xs inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Add</button>
        </div>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {emotes.length === 0 && (
            <div className="text-xs text-zinc-500 sc-mono py-2">No sub emotes yet. Click Add to create one.</div>
          )}
          {emotes.map((e, i) => (
            <div key={i} className="grid grid-cols-[64px_120px_1fr_36px] gap-2 items-center" data-testid={`sub-emote-row-${i}`}>
              <input
                data-testid={`sub-emote-char-${i}`}
                value={e.char}
                onChange={ev => updateRow(i, "char", ev.target.value)}
                className="sc-input text-2xl text-center h-12"
                title="Paste an emoji or one character"
                maxLength={16}
              />
              <input
                data-testid={`sub-emote-key-${i}`}
                value={e.key}
                onChange={ev => updateRow(i, "key", ev.target.value)}
                className="sc-input text-xs"
                placeholder="key (a-z, 0-9, _-)"
                maxLength={24}
              />
              <input
                data-testid={`sub-emote-label-${i}`}
                value={e.label}
                onChange={ev => updateRow(i, "label", ev.target.value)}
                className="sc-input text-xs"
                placeholder="Human label"
                maxLength={48}
              />
              <button
                data-testid={`sub-emote-remove-${i}`}
                onClick={() => removeRow(i)}
                className="p-2 rounded-sm border border-white/10 hover:border-[color:var(--sc-pink)] hover:text-[color:var(--sc-pink)] transition"
              ><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>

        <button
          onClick={saveEmotes}
          disabled={emoteBusy}
          data-testid="sub-emotes-save"
          className="sc-btn text-xs mt-3 inline-flex items-center gap-1.5"
        ><Save className="w-3.5 h-3.5" /> {emoteBusy ? "Saving…" : "Save emote pack"}</button>
      </div>
    </div>
  );
}
